import { useState } from 'react';
import type { DataJob } from '@cloudsign/shared';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Badge, Button, Card, EmptyState, FilterBar, Pager, SelectField, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorPanel } from '../../components/ErrorPanel';
import { downloadUrl, isRunning, useJob, useJobs } from './api';

const TONE: Record<DataJob['status'], 'neutral' | 'good' | 'warn' | 'bad'> = { queued: 'neutral', running: 'warn', done: 'good', failed: 'bad', blocked: 'bad' };

export function JobStatus({ status }: { status: DataJob['status'] }) {
  return <Badge tone={TONE[status]}>{t(`p4dp.job.status.${status}`)}</Badge>;
}

/** Export and erasure jobs, newest first, with a detail card that follows a running job. */
export function JobsTab({ openId, onOpen }: { openId: string | null; onOpen: (id: string | null) => void }) {
  const [kind, setKind] = useState<'export' | 'erasure' | ''>('');
  const [page, setPage] = useState(1);
  const q = useJobs(kind || undefined, page);
  return (
    <div className="space-y-4">
      {openId ? <JobDetail id={openId} onClose={() => onOpen(null)} /> : null}
      <FilterBar columns="sm:grid-cols-3">
        <SelectField
          label={t('p4dp.job.filterKind')}
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as typeof kind);
            setPage(1);
          }}
        >
          <option value="">{t('common.all')}</option>
          <option value="export">{t('p4dp.job.kind.export')}</option>
          <option value="erasure">{t('p4dp.job.kind.erasure')}</option>
        </SelectField>
      </FilterBar>
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : q.data.items.length === 0 ? (
        <EmptyState message={t('p4dp.job.empty')} />
      ) : (
        <>
          <TableWrap caption={t('p4dp.tab.jobs')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('p4dp.job.col.kind')}</Th>
                <Th>{t('p4dp.job.col.person')}</Th>
                <Th>{t('p4dp.job.col.status')}</Th>
                <Th>{t('p4dp.job.col.started')}</Th>
                <Th>{t('p4dp.job.col.by')}</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.map((j) => (
                <tr key={j.id}>
                  <td className="px-4 py-2.5">{t(`p4dp.job.kind.${j.kind}`)}</td>
                  <td className="px-4 py-2.5 font-medium">{j.subjectLabel}</td>
                  <td className="px-4 py-2.5">
                    <JobStatus status={j.status} />
                  </td>
                  <td className="px-4 py-2.5">{formatDateTime(j.createdAt)}</td>
                  <td className="px-4 py-2.5">{j.createdByLabel}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button onClick={() => onOpen(j.id)} aria-label={t('p4dp.job.openFor', { person: j.subjectLabel })}>
                      {t('p4dp.job.open')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={setPage} />
        </>
      )}
    </div>
  );
}

function JobDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useJob(id);
  const canDownload = useCan('data_protection.export');
  const j = q.data;
  const done = j ? j.results.filter((r) => r.done).length : 0;
  const expired = j?.downloadUntil ? new Date(j.downloadUntil).getTime() < Date.now() : false;
  return (
    <Card
      title={t('p4dp.job.title')}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !j ? (
        <Spinner />
      ) : (
        <div className="space-y-4" aria-live="polite">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium">
              {t(`p4dp.job.kind.${j.kind}`)}: {j.subjectLabel}
            </span>
            <JobStatus status={j.status} />
          </div>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-600">{t('p4dp.job.reason')}</dt>
              <dd>{j.reason}</dd>
            </div>
            <div>
              <dt className="text-slate-600">{t('p4dp.job.col.started')}</dt>
              <dd>
                {formatDateTime(j.createdAt)} ({j.createdByLabel})
              </dd>
            </div>
            {j.finishedAt ? (
              <div>
                <dt className="text-slate-600">{t('p4dp.job.finished')}</dt>
                <dd>{formatDateTime(j.finishedAt)}</dd>
              </div>
            ) : null}
          </dl>
          {j.errorKey ? (
            <Alert kind="error">
              <strong className="block">{t('p4dp.job.error')}</strong>
              {t(j.errorKey)}
            </Alert>
          ) : null}
          {isRunning(j) ? (
            <div>
              <p className="text-sm">{t('p4dp.job.progressCount', { done, total: j.results.length })}</p>
              <progress className="mt-1 h-2 w-full" max={Math.max(j.results.length, 1)} value={done} aria-label={t('p4dp.job.progress')} />
            </div>
          ) : null}
          {j.kind === 'export' && j.status === 'done' && canDownload ? (
            expired ? (
              <Alert kind="info">{t('p4dp.job.expired')}</Alert>
            ) : (
              <p className="flex flex-wrap items-center gap-3">
                <a href={downloadUrl(j.id)} className="inline-flex min-h-10 items-center rounded-md border border-transparent bg-brand px-3.5 py-1.5 text-sm font-medium text-on-brand hover:bg-brand-hover sm:min-h-9">
                  {t('p4dp.job.download')}
                </a>
                {j.downloadUntil ? <span className="text-sm text-slate-600">{t('p4dp.job.downloadUntil', { when: formatDateTime(j.downloadUntil) })}</span> : null}
              </p>
            )
          ) : null}
          {j.results.length > 0 ? (
            <TableWrap caption={t('p4dp.job.results')}>
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <Th>{t('p4dp.erase.module')}</Th>
                  <Th>{t('p4dp.erase.erased')}</Th>
                  <Th>{t('p4dp.erase.anonymised')}</Th>
                  <Th>{t('p4dp.erase.kept')}</Th>
                  <Th>{t('p4dp.erase.why')}</Th>
                  <Th>{t('p4dp.job.col.status')}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {j.results.map((r) => (
                  <tr key={r.module}>
                    <td className="px-4 py-2.5 font-medium">{r.module}</td>
                    <td className="px-4 py-2.5">{r.erased}</td>
                    <td className="px-4 py-2.5">{r.anonymised}</td>
                    <td className="px-4 py-2.5">{r.kept}</td>
                    <td className="px-4 py-2.5 text-slate-700">{r.keepReasonKey ? t(r.keepReasonKey) : ''}</td>
                    <td className="px-4 py-2.5">
                      <Badge tone={r.done ? 'good' : 'neutral'}>{r.done ? t('p4dp.job.moduleDone') : t('p4dp.job.modulePending')}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          ) : null}
        </div>
      )}
    </Card>
  );
}
